import { Fragment, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { Child, Container, Header, LayerContent } from "../style";
import { ChartContainer, NoData } from "../../style";
import { TableContainer } from "../../../Users/User.style";
import { barOptions } from "../formUtils";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";
import SectionModal from "./SectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";

// Section 6 — Overview of Shift and Incident Time. Mirrors the Fall Analysis layout: the three shift
// grids stacked on the left, with the 3-hour-band and shift-total bar charts on the right. Both charts
// are click-through (band -> drillDownByBand, shift total -> drillDownByShift).
// Reads woundAnalysisIncidents.shiftAndTime: { "Morning Shift": [{ shift, count, percentage }], ...,
//   drillDownByBand: { <band>: incident[] }, drillDownByShift: { <shift>: incident[] } }
const SHIFT_ORDER = ["Morning Shift", "Afternoon Shift", "Night Shift"];

// Each shift's bands are colored with that shift's shade family — matches the Fall Analysis time chart:
// morning = blues, afternoon = greens/teals, night = reds.
const SHIFT_SHADES: Record<string, string[]> = {
  "Morning Shift": MITIGATE_COLORS.BLUE,
  "Afternoon Shift": MITIGATE_COLORS.GREEN,
  "Night Shift": MITIGATE_COLORS.RED,
};
// The 8-hour span each shift covers — matches the Fall Analysis shift-total chart labels.
const SHIFT_TOTAL_LABELS: Record<string, string> = {
  "Morning Shift": "7am - 3pm",
  "Afternoon Shift": "3pm - 11pm",
  "Night Shift": "11pm - 7am",
};

const buildWithTotal = (rows: any[]) => {
  if (!rows?.length) return [];
  const out = rows.map((r) => ({
    shift: r.shift,
    count: r.count,
    percentage: r.percentage,
  }));
  const totalCount = out.reduce((s, r) => s + (r.count ?? 0), 0);
  const totalPct = out.reduce((s, r) => s + (Number(r.percentage) || 0), 0);
  out.push({ shift: "Total", count: totalCount, percentage: totalPct });
  return out;
};

const ShiftGrid = ({ shiftName, rows }: { shiftName: string; rows: any[] }) => {
  const cellCountProps = {
    cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
  };
  const cellPercentageProps = {
    cells: { data: CenteredWholeNumCell, append: "%", headerCell: HeaderCenteredCell },
  };
  return (
    <Fragment>
      <Grid data={rows || []} scrollable="none" rows={{ data: TotalRow }}>
        <GridColumn field="shift" title={shiftName} />
        <GridColumn field="count" title="#" {...(cellCountProps as GridColumnProps)} />
        <GridColumn field="percentage" title="%" {...(cellPercentageProps as GridColumnProps)} />
      </Grid>
    </Fragment>
  );
};

const ShiftAndTime = ({ setClickedIncident }: any) => {
  const [shiftData, setShiftData] = useState<Record<string, any[]>>({});
  const [shiftTotalsBar, setShiftTotalsBar] = useState<any>(null);
  const [bandBar, setBandBar] = useState<any>(null);
  const [selected, setSelected] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  useEffect(() => {
    const section = woundAnalysisIncidents?.shiftAndTime;
    if (section) {
      const next: Record<string, any[]> = {};
      SHIFT_ORDER.forEach((s) => {
        next[s] = buildWithTotal(section[s]);
      });
      setShiftData(next);

      // Shift totals (Morning / Afternoon / Night) — one bar per shift, blue / teal / red like Fall.
      const totals = SHIFT_ORDER.map((s) =>
        (section[s] || []).reduce((sum: number, r: any) => sum + (r.count ?? 0), 0)
      );
      setShiftTotalsBar({
        labels: SHIFT_ORDER.map((s) => SHIFT_TOTAL_LABELS[s]),
        datasets: [
          {
            label: "Shift",
            backgroundColor: SHIFT_ORDER.map((s) => SHIFT_SHADES[s][0]),
            borderWidth: 1,
            data: totals,
          },
        ],
      });

      // 3-hour band breakdown across all shifts, each band shaded by its shift family
      // (morning = blues, afternoon = greens, night = reds) to match the Fall time chart.
      const bandLabels: string[] = [];
      const bandCounts: number[] = [];
      const bandColors: string[] = [];
      SHIFT_ORDER.forEach((s) => {
        const shades = SHIFT_SHADES[s];
        (section[s] || []).forEach((r: any, i: number) => {
          bandLabels.push(r.shift);
          bandCounts.push(r.count ?? 0);
          bandColors.push(shades[i % shades.length]);
        });
      });
      setBandBar({
        labels: bandLabels,
        datasets: [
          {
            label: "Incident Time",
            backgroundColor: bandColors,
            borderWidth: 1,
            data: bandCounts,
          },
        ],
      });
    } else {
      setShiftData({});
      setShiftTotalsBar(null);
      setBandBar(null);
    }
  }, [woundAnalysisIncidents]);

  const title = "Overview of Shift and Incident Time";
  const hasData = SHIFT_ORDER.some((s) => (shiftData[s]?.length ?? 0) > 0);

  // Click a 3-hour band -> incidents that fell in that band.
  const handleBandClicked = (chart: any) => {
    if (!chart.length) return;
    const label = bandBar?.labels?.[chart[0].index];
    setSelected(label);
    setDetails(
      woundAnalysisIncidents?.shiftAndTime?.drillDownByBand?.[label] || []
    );
    setShowModal(true);
  };

  // Click a shift total -> all incidents in that shift.
  const handleShiftClicked = (chart: any) => {
    if (!chart.length) return;
    const shiftName = SHIFT_ORDER[chart[0].index];
    setSelected(SHIFT_TOTAL_LABELS[shiftName] ?? shiftName);
    setDetails(
      woundAnalysisIncidents?.shiftAndTime?.drillDownByShift?.[shiftName] || []
    );
    setShowModal(true);
  };

  return (
    <LayerContent>
      <Header>{title}</Header>
      {hasData ? (
        <Container>
          <Child flex="40%">
            {SHIFT_ORDER.map((s) => (
              <TableContainer key={s}>
                <ShiftGrid shiftName={s} rows={shiftData[s] || []} />
              </TableContainer>
            ))}
          </Child>
          <Child flex="60%">
            <ChartContainer>
              {bandBar ? (
                <Bar options={barOptions(handleBandClicked)} data={bandBar} />
              ) : (
                <NoData>No Data</NoData>
              )}
            </ChartContainer>
            <ChartContainer style={{ marginTop: "20px" }}>
              {shiftTotalsBar ? (
                <Bar options={barOptions(handleShiftClicked)} data={shiftTotalsBar} />
              ) : (
                <NoData>No Data</NoData>
              )}
            </ChartContainer>
          </Child>
        </Container>
      ) : (
        <NoData>No Data</NoData>
      )}
      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={woundFacility?.label ?? woundFacility?.[0]?.label}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={details}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${selected}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: details,
              columnData: woundAnalysisDetailCols(),
              fileName: woundFacility?.label ?? woundFacility?.[0]?.label,
              startDate,
              endDate,
              subTitle: `${title} - ${selected}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              details,
              woundAnalysisDetailCols(),
              `${woundFacility?.label ?? woundFacility?.[0]?.label} - ${title} - ${selected}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default ShiftAndTime;
